import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { StrictMode, useState } from 'react';
import { createRoot } from 'react-dom/client';
import { BrowserRouter, Navigate, Route, Routes, useParams } from 'react-router';
import './styles.css';
import { useMe } from './api/queries.js';
import { AuthProvider } from './auth/AuthProvider.js';
import { Masthead } from './components/Masthead.js';
import { RequireAuth } from './components/RequireAuth.js';
import { useAppTheme } from './theme/applyTheme.js';
import { BoardPage } from './routes/BoardPage.js';
import { Callback } from './routes/Callback.js';
import { Dashboard } from './routes/Dashboard.js';
import { Home } from './routes/Home.js';
import { TaskPage } from './boards/tasks/TaskPage.js';
import { PrivacyPage } from './routes/PrivacyPage.js';
import { SettingsPage } from './routes/SettingsPage.js';
import './boards/tasks/index.js';
import './boards/checklist/index.js';
import './boards/text/index.js';
import './boards/link/index.js';
import './boards/calendar/index.js';

const root = document.getElementById('root');
if (!root) throw new Error('#root not found');

const queryClient = new QueryClient({
  defaultOptions: { queries: { retry: 1, staleTime: 30_000 } },
});

/** The old members-page URL — kept working as a redirect for any existing bookmarks/links. */
function MembersRedirect() {
  const { householdId } = useParams<{ householdId: string }>();
  return <Navigate to={`/households/${householdId}/settings`} replace />;
}

function App() {
  const [selectedHouseholdId, setSelectedHouseholdId] = useState<string | null>(null);
  // The dashboard route (see routes/Dashboard.tsx) has no Cognito session
  // most of the time, so `data` here is undefined there and this resolves
  // to the plain light preset — harmless, since Dashboard applies its own
  // device theme (via ThemeScope) around its actual content regardless.
  const { data: me } = useMe();
  useAppTheme(me?.theme);

  return (
    <Masthead selectedHouseholdId={selectedHouseholdId} onSelectHousehold={setSelectedHouseholdId}>
      <Routes>
        <Route path="/" element={<Home selectedHouseholdId={selectedHouseholdId} />} />
        <Route path="/callback" element={<Callback />} />
        <Route path="/privacy" element={<PrivacyPage />} />
        <Route path="/dashboard" element={<Dashboard />} />
        <Route
          path="/households/:householdId/boards/:boardId"
          element={
            <RequireAuth>
              <BoardPage />
            </RequireAuth>
          }
        />
        <Route
          path="/households/:householdId/boards/:boardId/tasks/:taskId"
          element={
            <RequireAuth>
              <TaskPage />
            </RequireAuth>
          }
        />
        <Route
          path="/households/:householdId/settings"
          element={
            <RequireAuth>
              <SettingsPage />
            </RequireAuth>
          }
        />
        <Route path="/households/:householdId/members" element={<MembersRedirect />} />
      </Routes>
    </Masthead>
  );
}

createRoot(root).render(
  <StrictMode>
    <BrowserRouter>
      <AuthProvider>
        <QueryClientProvider client={queryClient}>
          <App />
        </QueryClientProvider>
      </AuthProvider>
    </BrowserRouter>
  </StrictMode>,
);
